import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CreditCard, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { t } from "@/i18n";

const money = (n: number) => `$${n.toFixed(2)}`;

export default function Billing() {
  const api = useApi();
  const [b, setB] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/billing").then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!b) return <p className="page faint">{t("Loading…")}</p>;
  const active = b.status === "active";
  const togglePortal = async () => {
    try { await api.put("/billing/portal", { portal: !b.portal }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="page max-w-2xl stack" data-testid="billing-page">
      <div>
        <p className="mono-label">{t("Owner only")}</p>
        <h1 className="text-3xl font-bold tracking-tight mt-1">{t("Billing")}</h1>
      </div>
      <div className="card stack-sm">
        <div className="flex items-center justify-between">
          <p className="mono-label flex items-center gap-2"><CreditCard size={13} /> {t("Monthly plan")}</p>
          <span className={`chip ${active ? "chip-ok" : b.status === "past_due" ? "chip-err" : "chip-warn"}`} data-testid="billing-status">{t(active ? "Active" : b.status === "past_due" ? "Payment failed" : "Not subscribed")}</span>
        </div>
        <div className="divide-y divide-[var(--border-color)] text-sm">
          <div className="flex justify-between py-2"><span>{t("SMBOI plan (includes owner)")}</span><span>{money(b.prices.smboi_base_monthly)}</span></div>
          <div className="flex justify-between py-2" data-testid="billing-seats"><span>{t("Extra team seats")} × {b.extra_seats}</span><span>{money(b.prices.smboi_seat_monthly * b.extra_seats)}</span></div>
          <label className="flex justify-between py-2 cursor-pointer">
            <span className="flex items-center gap-2"><input type="checkbox" checked={b.portal} onChange={togglePortal} className="accent-[var(--accent-primary)]" data-testid="billing-portal-toggle" /> {t("Customer portal")}</span>
            <span>{money(b.portal ? b.prices.smboi_portal_monthly : 0)}</span>
          </label>
          <div className="flex justify-between py-2 font-semibold"><span>{t("Total per month")}</span><span data-testid="billing-total">{money(b.total)}</span></div>
        </div>
        <p className="text-xs faint">{t("Plus applicable tax.")} {t("Each extra team member adds a seat. Changes are prorated automatically once you're subscribed.")}</p>
        {active
          ? <p className="text-sm muted" data-testid="billing-active-since">{t("Subscribed since")} {fmtDate(b.since)}</p>
          : <button className="btn btn-primary" disabled={busy} onClick={checkout} data-testid="billing-checkout-button">{busy ? t("Opening checkout…") : t("Subscribe")}</button>}
        {b.mode === "test" && <p className="text-xs faint" data-testid="billing-test-mode">{t("Test mode: use card 4242 4242 4242 4242, any future date and any CVC.")}</p>}
      </div>
    </div>
  );
}

export function PaymentResult({ ok }: { ok: boolean }) {
  const api = useApi();
  const [params] = useSearchParams();
  const [state, setState] = useState(ok ? "checking" : "cancel");
  useEffect(() => {
    const id = params.get("session_id");
    if (!ok || !id) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${id}`);
        if (data.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(data.payment_status)) return setState("failed");
      } catch { /* keep polling */ }
      if (++tries < 15) setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
  }, [api, ok, params]);
  const msg: Record<string, string> = { checking: "Confirming your payment…", paid: "You're subscribed. Thank you!",
    failed: "The payment didn't go through.", timeout: "Still confirming — check Billing again in a minute.", cancel: "Checkout was cancelled. Nothing was charged." };
  return (
    <div className="page max-w-lg stack-sm items-start" data-testid="payment-result">
      {state === "checking" ? <Loader2 className="animate-spin" /> : state === "paid" ? <CheckCircle2 className="text-[var(--status-ok)]" /> : <XCircle className="text-[var(--status-error)]" />}
      <p className="text-xl font-semibold" data-testid={`payment-${state}`}>{t(msg[state])}</p>
      <Link to="/billing" className="btn btn-ghost" data-testid="payment-back-billing">{t("Back to Billing")}</Link>
    </div>
  );
}
